/**
 * Accessibility - IMPROVEMENT 29
 * Accessibility utilities and components.
 */
import { useEffect, useState, ReactNode, createContext, useContext, useCallback } from 'react'
import clsx from 'clsx'
import './Accessibility.css'

// Skip Link for keyboard navigation
interface SkipLinkProps {
  href?: string
  children?: ReactNode
}

export function SkipLink({ href = '#main-content', children = 'Skip to main content' }: SkipLinkProps) {
  return (
    <a href={href} className="skip-link">
      {children}
    </a>
  )
}

// Screen reader only text
interface SrOnlyProps {
  children: ReactNode
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export function SrOnly({ children, as: Component = 'span' }: SrOnlyProps) {
  return <Component className="sr-only">{children}</Component>
}

// Live region for announcements
interface LiveRegionProps {
  message: string
  politeness?: 'polite' | 'assertive'
  clearAfter?: number
}

export function LiveRegion({ message, politeness = 'polite', clearAfter = 5000 }: LiveRegionProps) {
  const [currentMessage, setCurrentMessage] = useState(message)

  useEffect(() => {
    setCurrentMessage(message)
    if (clearAfter > 0) {
      const timer = setTimeout(() => setCurrentMessage(''), clearAfter)
      return () => clearTimeout(timer)
    }
  }, [message, clearAfter])

  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      className="sr-only"
    >
      {currentMessage}
    </div>
  )
}

// Focus trap for modals
interface FocusTrapProps {
  children: ReactNode
  active?: boolean
  onEscape?: () => void
}

export function FocusTrap({ children, active = true, onEscape }: FocusTrapProps) {
  useEffect(() => {
    if (!active) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        onEscape()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [active, onEscape])

  return <>{children}</>
}

// Reduced motion context
interface ReducedMotionContextType {
  prefersReducedMotion: boolean
}

const ReducedMotionContext = createContext<ReducedMotionContextType>({ prefersReducedMotion: false })

export function ReducedMotionProvider({ children }: { children: ReactNode }) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mq.matches)

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return (
    <ReducedMotionContext.Provider value={{ prefersReducedMotion }}>
      {children}
    </ReducedMotionContext.Provider>
  )
}

export function useReducedMotion() {
  return useContext(ReducedMotionContext).prefersReducedMotion
}

// Keyboard navigation hook
export function useKeyboardNav<T>(
  items: T[],
  options: {
    onSelect?: (item: T, index: number) => void
    loop?: boolean
    initialIndex?: number
  } = {}
) {
  const { onSelect, loop = true, initialIndex = -1 } = options
  const [focusedIndex, setFocusedIndex] = useState(initialIndex)

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault()
        setFocusedIndex(prev => {
          const next = prev + 1
          if (next >= items.length) return loop ? 0 : prev
          return next
        })
        break
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault()
        setFocusedIndex(prev => {
          const next = prev - 1
          if (next < 0) return loop ? items.length - 1 : 0
          return next
        })
        break
      case 'Home':
        e.preventDefault()
        setFocusedIndex(0)
        break
      case 'End':
        e.preventDefault()
        setFocusedIndex(items.length - 1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (focusedIndex >= 0 && onSelect) {
          onSelect(items[focusedIndex], focusedIndex)
        }
        break
    }
  }, [items, focusedIndex, loop, onSelect])

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
    getItemProps: (index: number) => ({
      tabIndex: focusedIndex === index ? 0 : -1,
      'aria-selected': focusedIndex === index,
      onFocus: () => setFocusedIndex(index)
    })
  }
}

// Focus visible polyfill hook
export function useFocusVisible() {
  const [focusVisible, setFocusVisible] = useState(false)
  const [hadKeyboard, setHadKeyboard] = useState(false)

  useEffect(() => {
    const handleKeyDown = () => setHadKeyboard(true)
    const handleMouseDown = () => setHadKeyboard(false)

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  return {
    focusVisible,
    onFocus: () => setFocusVisible(hadKeyboard),
    onBlur: () => setFocusVisible(false)
  }
}

// High contrast mode detector
export function useHighContrast() {
  const [highContrast, setHighContrast] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(forced-colors: active)')
    setHighContrast(mq.matches)

    const handler = (e: MediaQueryListEvent) => setHighContrast(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return highContrast
}

// Accessible loading indicator
interface AccessibleSpinnerProps {
  label?: string
  size?: 'sm' | 'md' | 'lg'
}

export function AccessibleSpinner({ label = 'Loading', size = 'md' }: AccessibleSpinnerProps) {
  return (
    <div className={clsx('accessible-spinner', `accessible-spinner--${size}`)} role="status">
      <svg viewBox="0 0 24 24" fill="none" className="accessible-spinner-svg">
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="31.4 31.4"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  )
}

// Accessible progress bar
interface AccessibleProgressProps {
  value: number
  max?: number
  label?: string
  showValue?: boolean
}

export function AccessibleProgress({
  value,
  max = 100,
  label = 'Progress',
  showValue = true
}: AccessibleProgressProps) {
  const percentage = Math.round((value / max) * 100)

  return (
    <div className="accessible-progress">
      {label && <div className="accessible-progress-label">{label}</div>}
      <div
        className="accessible-progress-track"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
      >
        <div
          className="accessible-progress-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showValue && <div className="accessible-progress-value">{percentage}%</div>}
    </div>
  )
}

export default SkipLink
